import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Check, 
  Pencil, 
  History, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanningValidationBlock() {
  const { t } = useLanguage();
  const [pendingSlots, setPendingSlots] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showModifyDialog, setShowModifyDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [modifyForm, setModifyForm] = useState({
    proposed_start_time: '',
    proposed_end_time: '',
    proposed_break_minutes: 0,
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchPendingSlots = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/planning-validations/pending`);
      setPendingSlots(res.data);
    } catch (err) {
      console.error('Error fetching pending slots:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/planning-validations/history`);
      setHistory(res.data);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  }, []);

  useEffect(() => {
    fetchPendingSlots();
    fetchHistory();
  }, [fetchPendingSlots, fetchHistory]);

  const handleValidateDay = async (scheduleId) => {
    try {
      await axios.post(`${API}/planning-validations`, {
        schedule_id: scheduleId,
        validation_type: 'confirm'
      });
      toast.success(t('dayValidated'));
      fetchPendingSlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const handleValidateAll = async () => {
    if (pendingSlots.length === 0) return;
    
    try {
      setSubmitting(true);
      const scheduleIds = pendingSlots.map(s => s.id);
      const res = await axios.post(`${API}/planning-validations/bulk`, scheduleIds);
      toast.success(`${res.data.count} ${t('allDaysValidated')}`);
      fetchPendingSlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const openModifyDialog = (slot) => {
    setSelectedSlot(slot);
    setModifyForm({
      proposed_start_time: slot.start_time,
      proposed_end_time: slot.end_time,
      proposed_break_minutes: slot.break_minutes || 0,
      reason: ''
    });
    setShowModifyDialog(true);
  };

  const handleSubmitModification = async () => {
    if (!modifyForm.reason.trim()) {
      toast.error(t('reasonRequired'));
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/planning-validations`, {
        schedule_id: selectedSlot.id,
        validation_type: 'modify',
        proposed_start_time: modifyForm.proposed_start_time,
        proposed_end_time: modifyForm.proposed_end_time,
        proposed_break_minutes: modifyForm.proposed_break_minutes,
        reason: modifyForm.reason
      });
      toast.success(t('modificationSubmitted'));
      setShowModifyDialog(false);
      fetchPendingSlots();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    return `${days[date.getDay()]} ${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'approved': return t('modificationApproved');
      case 'rejected': return t('modificationRejected');
      case 'pending': return t('modificationPending');
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="border border-border rounded-lg p-4 bg-card">
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border border-border rounded-lg p-4 bg-card" data-testid="planning-validation-block">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">{t('validateMyPlanning').toUpperCase()}</h3>
            {pendingSlots.length > 0 && (
              <span className="bg-amber-500/20 text-amber-600 text-xs px-2 py-0.5 rounded-full">
                {pendingSlots.length} {t('pendingValidationDays')}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory(true)}
            className="text-muted-foreground hover:text-foreground"
            data-testid="validation-history-btn"
          >
            <History className="h-4 w-4 mr-1" />
            {t('validationHistory')}
          </Button>
        </div>

        {pendingSlots.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <CheckCircle className="h-5 w-5 mr-2 text-emerald-500" />
            {t('noValidationPending')}
          </div>
        ) : (
          <>
            {/* Header row */}
            <div className="grid grid-cols-[100px_80px_80px_60px_80px_1fr] gap-2 px-2 py-1 text-xs text-muted-foreground font-medium border-b border-border mb-2">
              <div>{t('date')}</div>
              <div>{t('startTime')}</div>
              <div>{t('endTime')}</div>
              <div>{t('break')}</div>
              <div>{t('effectiveTime')}</div>
              <div className="text-right">{t('actions')}</div>
            </div>

            {/* Scrollable list */}
            <ScrollArea className={pendingSlots.length > 5 ? "h-[200px]" : ""}>
              <div className="space-y-1">
                {pendingSlots.map((slot) => (
                  <div
                    key={slot.id}
                    className="grid grid-cols-[100px_80px_80px_60px_80px_1fr] gap-2 items-center px-2 py-2 rounded hover:bg-muted/50 text-sm"
                    data-testid={`validation-slot-${slot.id}`}
                  >
                    <div className="font-medium">{formatDate(slot.date)}</div>
                    <div>{slot.start_time}</div>
                    <div>{slot.end_time}</div>
                    <div className="text-muted-foreground">{slot.break_minutes || 0}min</div>
                    <div className="font-semibold text-primary">{slot.effective_hours}h</div>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-emerald-600 hover:bg-emerald-500/10 hover:text-emerald-600"
                        onClick={() => handleValidateDay(slot.id)}
                        title={t('conformSchedule')}
                        data-testid={`validate-btn-${slot.id}`}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600"
                        onClick={() => openModifyDialog(slot)}
                        title={t('nonConformSchedule')}
                        data-testid={`modify-btn-${slot.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Validate all button */}
            <div className="mt-4 pt-3 border-t border-border flex justify-end">
              <Button
                onClick={handleValidateAll}
                disabled={submitting || pendingSlots.length === 0}
                className="btn-vitalis"
                data-testid="validate-all-btn"
              >
                <Check className="h-4 w-4 mr-2" />
                {t('validateAll')} ({pendingSlots.length})
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Modify Dialog */}
      <Dialog open={showModifyDialog} onOpenChange={setShowModifyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              {t('modifySchedule')}
            </DialogTitle>
          </DialogHeader>
          {selectedSlot && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium">{formatDate(selectedSlot.date)}</p>
                <p className="text-xs text-muted-foreground">
                  {t('plannedHours')}: {selectedSlot.start_time} - {selectedSlot.end_time}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">{t('proposedStart')}</label>
                  <Input
                    type="time"
                    value={modifyForm.proposed_start_time}
                    onChange={(e) => setModifyForm({ ...modifyForm, proposed_start_time: e.target.value })}
                    data-testid="proposed-start-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('proposedEnd')}</label>
                  <Input
                    type="time"
                    value={modifyForm.proposed_end_time}
                    onChange={(e) => setModifyForm({ ...modifyForm, proposed_end_time: e.target.value })}
                    data-testid="proposed-end-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">{t('proposedBreak')} (min)</label>
                <Input
                  type="number"
                  min="0"
                  value={modifyForm.proposed_break_minutes}
                  onChange={(e) => setModifyForm({ ...modifyForm, proposed_break_minutes: parseInt(e.target.value) || 0 })}
                  data-testid="proposed-break-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium">{t('modificationReason')} *</label>
                <Textarea
                  value={modifyForm.reason}
                  onChange={(e) => setModifyForm({ ...modifyForm, reason: e.target.value })}
                  placeholder={t('reasonRequired')}
                  rows={3}
                  data-testid="modification-reason-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowModifyDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleSubmitModification}
                  disabled={submitting || !modifyForm.reason.trim()}
                  className="btn-vitalis"
                  data-testid="submit-modification-btn"
                >
                  {t('submitModification')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              {t('validationHistory')}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {history.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                {t('noValidationHistory')}
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="border border-border rounded-lg p-3"
                    data-testid={`history-item-${item.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{formatDate(item.date)}</span>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(item.status)}
                        <span className={`text-sm ${
                          item.status === 'approved' ? 'text-emerald-600' :
                          item.status === 'rejected' ? 'text-red-600' :
                          'text-amber-600'
                        }`}>
                          {getStatusText(item.status)}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>
                        <span className="font-medium">{t('plannedHours')}:</span> {item.original_start_time} - {item.original_end_time}
                      </p>
                      <p>
                        <span className="font-medium">{t('proposedStart')}:</span> {item.proposed_start_time} - {item.proposed_end_time}
                      </p>
                      <p>
                        <span className="font-medium">{t('reason')}:</span> {item.reason}
                      </p>
                      {item.review_notes && (
                        <p className="mt-1 pt-1 border-t border-border">
                          <span className="font-medium">Note:</span> {item.review_notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
